import { Alert, Box, Button, Group, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconRefresh, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  useExecuteBotMigration,
  usePreviewBotMigration,
} from 'src/secret-menu/hooks/useBotMigration';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type MigrationResult = ReturnType<typeof usePreviewBotMigration>['result'];

export default function BotMigrationTab() {
  const { preview, isLoading: isPreviewing, result: previewResult } = usePreviewBotMigration();
  const { execute, isLoading: isExecuting, result: executeResult } = useExecuteBotMigration();
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);

  useEffect(() => {
    if (previewResult) setMigrationResult(previewResult);
  }, [previewResult]);

  useEffect(() => {
    if (executeResult) setMigrationResult(executeResult);
  }, [executeResult]);

  const handlePreview = async () => {
    try {
      await preview();
      notifications.show({
        title: 'Preview complete',
        message: 'Bot migration preview has been generated for all companies.',
        color: 'blue',
      });
    } catch (error) {
      notifyAndLogError(
        'Failed to preview migration',
        'An error occurred while previewing the bot migration.',
        error,
      );
    }
  };

  const handleExecute = async () => {
    try {
      await execute();
      notifications.show({
        title: 'Migration complete',
        message: 'Bot names have been updated for all companies.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (error) {
      notifyAndLogError(
        'Failed to execute migration',
        'An error occurred while executing the bot migration.',
        error,
      );
    }
  };

  const totalMigrated = migrationResult?.results.reduce((sum, r) => sum + r.stats.migrated, 0) ?? 0;

  return (
    <Box p="md">
      <Stack gap="xl" maw={800}>
        <Stack gap="md">
          <Text fw={600} size="lg">
            Bot name migration (all companies)
          </Text>
          <Text size="sm" c="dimmed">
            Rename scheduled bots from &quot;Siesta&quot; to the tenant name for all companies. This
            will update bots that are in READY state (not yet joined meetings).
          </Text>

          <Group>
            <Button
              variant="outline"
              leftSection={<IconSearch size={16} />}
              onClick={handlePreview}
              loading={isPreviewing}
            >
              Preview all companies
            </Button>
            <Button
              color="orange"
              leftSection={<IconRefresh size={16} />}
              onClick={handleExecute}
              loading={isExecuting}
              disabled={!migrationResult || totalMigrated === 0}
            >
              Execute migration
            </Button>
          </Group>

          {migrationResult && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title={migrationResult.dryRun ? 'Preview results' : 'Migration results'}
              color={migrationResult.dryRun ? 'blue' : 'green'}
            >
              <Stack gap="md">
                <Text size="sm" fw={500}>
                  Total companies: {migrationResult.totalCompanies}
                </Text>

                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Tenant</Table.Th>
                      <Table.Th>Checked</Table.Th>
                      <Table.Th>{migrationResult.dryRun ? 'Would migrate' : 'Migrated'}</Table.Th>
                      <Table.Th>Skipped (active)</Table.Th>
                      <Table.Th>Skipped (finished)</Table.Th>
                      <Table.Th>Failed</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {migrationResult.results.map(result => (
                      <Table.Tr key={result.tenantName}>
                        <Table.Td>{result.tenantName}</Table.Td>
                        <Table.Td>{result.stats.checked}</Table.Td>
                        <Table.Td>{result.stats.migrated}</Table.Td>
                        <Table.Td>{result.stats.skippedActive}</Table.Td>
                        <Table.Td>{result.stats.skippedFinished}</Table.Td>
                        <Table.Td c={result.stats.failed > 0 ? 'red' : undefined}>
                          {result.stats.failed}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Alert>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
